<script setup lang="ts">
import { ref, computed, type ComputedRef, onMounted, onUnmounted, toRefs } from 'vue'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import NumberInput from '@/components/lib/NumberInput.vue'
import { t } from '@/locales/i18n'

interface Filter {
  gametype: string
  position: string
  minStack: number
  maxStack: number
}

interface GmfSelectorFilter {
  gametype: string
  rows: Array<{ cells: Array<number> }>
  positions: Array<string>
  activeFilters: Array<Filter>
  inputsOpen: boolean
}

const emit = defineEmits<{
  (e: 'update:activeFilters', filters: Array<Filter>): void
  (e: 'update:inputsOpen', inputsOpen: boolean): void
}>()

const props = defineProps<GmfSelectorFilter>()
const { inputsOpen } = toRefs(props)
const minValue = 0
const maxValue = 999

const windowWidth = ref(0)
const updateWidth = () => {
  windowWidth.value = window.innerWidth
}
const isSmallScreen = computed(() => windowWidth.value < 1024)

onMounted(() => {
  updateWidth()
  window.addEventListener('resize', updateWidth)
})
onUnmounted(() => {
  window.removeEventListener('resize', updateWidth)
})

const filters: ComputedRef<Array<Filter>> = computed(() => {
  const positions = props.positions.map(position => {
    const existingActiveFilter = props.activeFilters.find(
      f => f.position === position && f.gametype === props.gametype,
    )

    if (existingActiveFilter) {
      return existingActiveFilter
    }

    const minStack = props.rows.reduce(
      (min, row) => Math.min(row.cells[props.positions.findIndex(p => p === position) + 1], min),
      maxValue,
    )
    const maxStack = props.rows.reduce(
      (max, row) => Math.max(row.cells[props.positions.findIndex(p => p === position) + 1], max),
      minValue,
    )

    return {
      gametype: props.gametype,
      position,
      minStack: minStack === maxValue ? minValue : minStack,
      maxStack,
    }
  })

  const avgExistingFilter = props.activeFilters.find(
    f => f.position === 'Avg.' && f.gametype === props.gametype,
  )

  const minStack = props.rows.reduce((min, row) => Math.min(row.cells[0], min), maxValue)
  const maxStack = props.rows.reduce((max, row) => Math.max(row.cells[0], max), minValue)

  const avgFilter = avgExistingFilter || {
    gametype: props.gametype,
    position: 'Avg.',
    minStack: minStack === maxValue ? minValue : minStack,
    maxStack,
  }

  return [avgFilter, ...positions]
})

function updateActiveFilter(filter: Filter) {
  const index = props.activeFilters.findIndex(
    f => f.position === filter.position && f.gametype === filter.gametype,
  )

  const existing = props.activeFilters[index]

  if (existing && existing.minStack === filter.minStack && existing.maxStack === filter.maxStack) {
    return
  }

  let updatedFilters: Filter[]
  if (index !== -1) {
    updatedFilters = [...props.activeFilters]
    updatedFilters[index] = filter
  } else {
    updatedFilters = [...props.activeFilters, filter]
  }

  emit('update:activeFilters', updatedFilters)
}

function removeActiveFilter(filter: Filter) {
  emit(
    'update:activeFilters',
    props.activeFilters.filter(
      f => !(f.position === filter.position && f.gametype === filter.gametype),
    ),
  )
}

function removeAllActiveFilters() {
  emit(
    'update:activeFilters',
    props.activeFilters.filter(
      f =>
        !(
          (props.positions.includes(f.position) || f.position === 'Avg.') &&
          f.gametype === props.gametype
        ),
    ),
  )
}
</script>

<template>
  <div class="filter-container">
    <div class="filter-header">
      <GwBtn
        variant="btn_default"
        :icon="inputsOpen ? 'mdi-filter' : 'mdi-filter-outline'"
        @click="emit('update:inputsOpen', !inputsOpen)"
      >
        <template #default>
          <span style="font-size: 1rem">
            {{ t(inputsOpen ? 'common.hide' : 'common.show') }}
            {{ t('common.filters').toLowerCase() }}
          </span>
        </template>
      </GwBtn>
      <div
        v-for="filter in activeFilters.filter(
          f =>
            f.gametype === props.gametype &&
            (props.positions.includes(f.position) || f.position === 'Avg.'),
        )"
        :key="filter.position"
        class="active-filter-item d-flex"
      >
        <div>{{ `${filter.position}, ${filter.minStack}-${filter.maxStack}` }}</div>
        <GwBtn
          variant="btn_default"
          class="remove-filter-btn"
          icon="mdi-close-circle"
          @click="removeActiveFilter(filter)"
        />
      </div>
      <GwBtn
        v-if="
          activeFilters.filter(
            f =>
              f.gametype === props.gametype &&
              (props.positions.includes(f.position) || f.position === 'Avg.'),
          ).length > 0
        "
        variant="btn_default"
        @click="removeAllActiveFilters"
        ><template #default
          ><span style="font-size: 1rem">{{ t('common.clear_all') }}</span></template
        ></GwBtn
      >
    </div>
    <Transition name="slide">
      <div
        v-if="inputsOpen"
        class="filter-inputs"
        :style="{ '--rows': Math.ceil(isSmallScreen ? filters.length : filters.length / 3) }"
      >
        <div
          v-for="(filter, index) in filters"
          :key="filter.position"
        >
          <div
            v-if="isSmallScreen ? index === 0 : index % Math.ceil(filters.length / 3) === 0"
            class="filter-inputs-header"
            :style="{
              'margin-left': isSmallScreen ? '6rem' : '4rem',
            }"
          >
            <div
              class="header-label"
              :style="{ 'margin-right': isSmallScreen ? '4.5rem' : '0.5rem' }"
            >
              {{ t('common.min') }}
            </div>
            <div class="header-label">{{ t('common.max') }}</div>
          </div>
          <div class="filter-input-item">
            <div style="text-align: right; width: 3.5rem; margin-right: 0.5rem">
              {{ filter.position }}
            </div>
            <NumberInput
              :value="filter.minStack"
              :min="minValue"
              :max="maxValue"
              :has-minus="isSmallScreen"
              :has-plus="isSmallScreen"
              @inputvalue="
                value => {
                  const parsed = isNaN(parseInt(value, 10)) ? 0 : parseInt(value, 10)
                  if (parsed !== filter.minStack) {
                    updateActiveFilter({ ...filter, minStack: parsed })
                  }
                }
              "
            />
            <NumberInput
              :value="filter.maxStack"
              :min="minValue"
              :max="maxValue"
              :has-minus="isSmallScreen"
              :has-plus="isSmallScreen"
              @inputvalue="
                value => {
                  const parsed = isNaN(parseInt(value, 10)) ? 0 : parseInt(value, 10)
                  if (parsed !== filter.maxStack) {
                    updateActiveFilter({ ...filter, maxStack: parsed })
                  }
                }
              "
            />
            <GwBtn
              :disabled="
                !activeFilters.find(
                  f => f.position === filter.position && f.gametype === filter.gametype,
                )
              "
              variant="btn_default"
              icon="mdi-close"
              @click="removeActiveFilter(filter)"
            />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.filter-container {
  font-size: 1rem;
  font-weight: 500;
}

.filter-inputs {
  display: grid;
  grid-template-rows: repeat(var(--rows), auto);
  grid-auto-flow: column;
  gap: 0.5em;
  background-color: var(--clr-black-2);
  border-radius: var(--app-rad-2);
  margin-top: 0.5em;
  padding: 0.5em;
  overflow: hidden;
  justify-content: space-evenly;
}

.filter-header {
  display: flex;
  align-items: center;
  gap: 0.5em;
  flex-wrap: wrap;
}

.active-filter-item {
  align-items: center;
  background-color: var(--clr-black-2);
  border-radius: var(--app-rad-2);
  padding: var(--app-pad-2) var(--app-pad-3);
}

.filter-input-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-inputs-header {
  display: flex;
  padding: 0 0.5rem;
  margin-left: 4rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--clr-grey-5);
}

.header-label {
  min-width: 2.75rem;
  text-align: center;
  margin-right: 0.5rem;
}

.remove-filter-btn {
  height: 1rem;
  width: 1rem;
  margin-left: 0.25rem;
  padding: 0;
  border-radius: 50%;
}

.slide-enter-active {
  animation: slideDown 0.3s ease-out forwards;
}

.slide-leave-active {
  animation: slideUp 0.3s ease-out forwards;
}

@keyframes slideDown {
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 500px;
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    max-height: 500px;
    opacity: 1;
  }
  to {
    max-height: 0;
    opacity: 0;
  }
}
</style>
